import { auth, isAccessTokenExpiring } from "./stores/auth.js";
import { get, writable } from "svelte/store";

const BASE = "/api";

// ─── MFA re-auth store ────────────────────────────────────────────────────────
// When a payment route returns MFA_REQUIRED or MFA_EXPIRED (403), the request()
// function pauses and sets a challenge on this store. The MfaReauthModal reads
// it and resolves/rejects when the user completes or dismisses re-auth.

export interface MfaChallenge {
	resolve: (token: string) => void;
	reject: (reason?: unknown) => void;
}

export const mfaChallenge = writable<MfaChallenge | null>(null);

export class ApiError extends Error {
	constructor(
		public readonly status: number,
		message: string,
		public readonly code?: string
	) {
		super(message);
		this.name = "ApiError";
	}

	/** User-safe message: 4xx errors are actionable, 5xx get a generic fallback. */
	get userMessage(): string {
		if (this.status >= 400 && this.status < 500) return this.message;
		return "Something went wrong — please try again";
	}
}

/**
 * Extract a user-safe error message from a caught exception.
 * 4xx ApiErrors pass through (actionable); 5xx and non-ApiErrors get a generic fallback.
 */
export function toastErrorMessage(e: unknown, fallback: string): string {
	if (e instanceof ApiError) return e.userMessage;
	return fallback;
}

// ─── Core fetch wrapper ───────────────────────────────────────────────────────

async function request<T>(
	path: string,
	opts: RequestInit & { skipAuth?: boolean } = {}
): Promise<T> {
	const { skipAuth, ...fetchOpts } = opts;

	const headers: Record<string, string> = {
		"Content-Type": "application/json",
		...(fetchOpts.headers as Record<string, string>),
	};

	if (!skipAuth) {
		// On a fresh page load the in-memory access token is null while the
		// layout's restoreSession() is still resolving the HttpOnly refresh
		// cookie. Without this wait, the dashboard's onMount fan-out fires
		// ~10 token-less requests that all 401, then each retries after the
		// refresh — a serial waterfall worth ~1.5s. Awaiting the single
		// in-flight restore here collapses that: every early call blocks on
		// the one refresh and then goes out already authenticated.
		if (!get(auth).accessToken && sessionRestorePromise) {
			await sessionRestorePromise;
		}
		// Proactively refresh an expired/near-expiry token so we don't fire a
		// request that 401s and then has to be retried. tryRefresh() is deduped,
		// so a burst of parallel calls triggers at most one /auth/token/refresh.
		if (isAccessTokenExpiring(get(auth))) {
			await tryRefresh();
		}
		const token = get(auth).accessToken;
		if (token) headers.Authorization = `Bearer ${token}`;
	}

	const res = await fetch(`${BASE}${path}`, { ...fetchOpts, headers });

	// Auto-refresh on 401
	if (res.status === 401 && !skipAuth) {
		const refreshed = await tryRefresh();
		if (refreshed) {
			headers.Authorization = `Bearer ${get(auth).accessToken}`;
			const retried = await fetch(`${BASE}${path}`, { ...fetchOpts, headers });
			if (!retried.ok) {
				const err = await retried.json().catch(() => ({ error: "Unknown error" }));
				const raw = (err as Record<string, unknown>).error;
				let msg = "Request failed";
				if (typeof raw === "string") {
					msg = raw;
				} else if (
					raw &&
					typeof raw === "object" &&
					Array.isArray((raw as { issues?: unknown[] }).issues)
				) {
					const firstIssue = (raw as { issues: { message: string }[] }).issues[0];
					if (firstIssue?.message) msg = firstIssue.message;
				}
				const code =
					typeof (err as Record<string, unknown>).code === "string"
						? ((err as Record<string, unknown>).code as string)
						: undefined;
				throw new ApiError(retried.status, msg, code);
			}
			return retried.json() as Promise<T>;
		} else {
			auth.logout();
			throw new ApiError(401, "Session expired");
		}
	}

	// MFA re-auth on 403 with known MFA codes — show inline re-challenge modal
	if (res.status === 403 && !skipAuth) {
		const body = await res.json().catch(() => ({})) as Record<string, unknown>;
		const code = body.code as string | undefined;
		if (code === "MFA_REQUIRED" || code === "MFA_EXPIRED") {
			let newToken: string;
			try {
				newToken = await new Promise<string>((resolve, reject) => {
					mfaChallenge.set({ resolve, reject });
				}).finally(() => mfaChallenge.set(null));
			} catch {
				throw new ApiError(403, "Verification required", code);
			}

			headers.Authorization = `Bearer ${newToken}`;
			const retried = await fetch(`${BASE}${path}`, { ...fetchOpts, headers });
			if (!retried.ok) {
				const err = await retried.json().catch(() => ({ error: "Unknown error" }));
				const raw = (err as Record<string, unknown>).error;
				let msg = "Request failed";
				if (typeof raw === "string") {
					msg = raw;
				} else if (
					raw &&
					typeof raw === "object" &&
					Array.isArray((raw as { issues?: unknown[] }).issues)
				) {
					const firstIssue = (raw as { issues: { message: string }[] }).issues[0];
					if (firstIssue?.message) msg = firstIssue.message;
				}
				const retryCode =
					typeof (err as Record<string, unknown>).code === "string"
						? ((err as Record<string, unknown>).code as string)
						: undefined;
				throw new ApiError(retried.status, msg, retryCode);
			}
			return retried.json() as Promise<T>;
		}
	}

	if (!res.ok) {
		const err = await res.json().catch(() => ({ error: "Unknown error" }));
		const raw = (err as Record<string, unknown>).error;
		let message = "Request failed";
		if (typeof raw === "string") {
			message = raw;
		} else if (
			raw &&
			typeof raw === "object" &&
			Array.isArray((raw as { issues?: unknown[] }).issues)
		) {
			const firstIssue = (raw as { issues: { message: string }[] }).issues[0];
			if (firstIssue?.message) message = firstIssue.message;
		}
		const code =
			typeof (err as Record<string, unknown>).code === "string"
				? ((err as Record<string, unknown>).code as string)
				: undefined;
		throw new ApiError(res.status, message, code);
	}

	return res.json() as Promise<T>;
}

async function downloadFile(path: string): Promise<void> {
	const token = get(auth).accessToken;
	const headers: Record<string, string> = {};
	if (token) headers.Authorization = `Bearer ${token}`;

	const res = await fetch(`${BASE}${path}`, { headers });

	if (res.status === 401) {
		const refreshed = await tryRefresh();
		if (refreshed) {
			headers.Authorization = `Bearer ${get(auth).accessToken}`;
			const retried = await fetch(`${BASE}${path}`, { headers });
			if (!retried.ok) throw new ApiError(retried.status, "Download failed");
			return triggerBlobDownload(retried);
		}
		auth.logout();
		throw new ApiError(401, "Session expired");
	}

	if (!res.ok) throw new ApiError(res.status, "Download failed");
	return triggerBlobDownload(res);
}

function triggerBlobDownload(res: Response): void {
	const disposition = res.headers.get("Content-Disposition") ?? "";
	const match = disposition.match(/filename="?(.+?)"?$/);
	const filename = match?.[1] ?? "export.csv";

	res.blob().then((blob) => {
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = filename;
		a.click();
		URL.revokeObjectURL(url);
	});
}

let inflightRefresh: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
	if (inflightRefresh) return inflightRefresh;

	inflightRefresh = (async () => {
		try {
			const data = await request<{ accessToken: string }>("/auth/token/refresh", {
				method: "POST",
				skipAuth: true,
			});
			auth.setTokens(data.accessToken);
			return true;
		} catch {
			return false;
		} finally {
			inflightRefresh = null;
		}
	})();

	return inflightRefresh;
}

/** In-flight session restore. Kicked off eagerly at module load (below) so the
 *  promise exists before ANY component's onMount runs — in SvelteKit child page
 *  onMount fires before the parent layout's, so the dashboard would otherwise
 *  fan out its calls before the layout could start the restore. request() awaits
 *  this so early authed calls block on the one refresh instead of 401-ing and
 *  retrying serially. */
let sessionRestorePromise: Promise<boolean> | null = null;

function startSessionRestore(): Promise<boolean> {
	if (sessionRestorePromise) return sessionRestorePromise;

	sessionRestorePromise = (async () => {
		const ok = await tryRefresh();
		if (!ok) {
			// Refresh failed — clear any stale cookie and in-memory state.
			await request<{ message: string }>("/auth/logout", { method: "POST", skipAuth: true }).catch(() => {});
			auth.logout();
		}
		return ok;
	})();

	// Intentionally NOT cleared on settle: request() guards on
	// `!accessToken && sessionRestorePromise`, so once a token exists the
	// wait is skipped anyway, and keeping the resolved promise lets any
	// stragglers await an already-settled value cheaply.
	return sessionRestorePromise;
}

// Eagerly restore the session as soon as this module loads in the browser, so
// the promise is in place before the first component mounts and fires requests.
if (typeof window !== "undefined") {
	void startSessionRestore();
}

/** Call once on app init to silently restore session from HttpOnly refresh cookie.
 *  Reuses the eager restore kicked off at module load (deduped). */
export async function restoreSession(): Promise<boolean> {
	return startSessionRestore();
}

// ─── Auth ─────────────────────────────────────────────────────────────────────

export const api = {
	auth: {
		sendOtp: (email: string, turnstileToken?: string) =>
			request<{ message: string }>("/auth/otp/send", {
				method: "POST",
				body: JSON.stringify({ email, turnstileToken }),
				skipAuth: true,
			}),

		verifyOtp: (email: string, code: string) =>
			request<{ accessToken: string }>("/auth/otp/verify", {
				method: "POST",
				body: JSON.stringify({ email, code }),
				skipAuth: true,
			}),

		logout: () => request<{ message: string }>("/auth/logout", { method: "POST" }),

		/**
		 * Current user's profile. Source of truth for the header greeting and
		 * the Settings page "Your name" field. Prefers `displayName`, then the
		 * cached Akahu first+last, then the email local-part (resolved in the
		 * UI — see PageHeader / Settings).
		 */
		me: () =>
			request<{
				id: string;
				email: string;
				displayName: string | null;
				akahuFirstName: string | null;
				akahuLastName: string | null;
			}>("/auth/me"),

		/** Update self-service profile fields. Pass `displayName: ''` (or null) to clear. */
		updateProfile: (body: { displayName?: string | null }) =>
			request<{
				id: string;
				email: string;
				displayName: string | null;
				akahuFirstName: string | null;
				akahuLastName: string | null;
			}>("/auth/me", {
				method: "PATCH",
				body: JSON.stringify(body),
			}),
	},

	// ── Web Push (browser notifications) ─────────────────────────────────────
	push: {
		vapidPublicKey: () =>
			request<{ publicKey: string }>("/push/vapid-public-key", { skipAuth: true }),

		subscribe: (body: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
			request<{ ok: boolean }>("/push/subscribe", {
				method: "POST",
				body: JSON.stringify(body),
			}),

		unsubscribe: (endpoint: string) =>
			request<{ ok: boolean }>("/push/unsubscribe", {
				method: "POST",
				body: JSON.stringify({ endpoint }),
			}),

		test: () =>
			request<{ sent: number; attempted: number; errors: string[] }>("/push/test", {
				method: "POST",
			}),
	},

	// ── Dashboard ──────────────────────────────────────────────────────────────
	dashboard: {
		get: () =>
			request<{
				connected: boolean;
				stats: {
					/** Roundup $ on settled txs in last 30d that have already been transferred */
					roundupsTransferred30d: number;
					txRoundupsTransferred30d: number;
					/** Roundup $ in last 30d still waiting for the batch transfer */
					roundupsQueued30d: number;
					txRoundupsQueued30d: number;
					totalTransferredAllTime: number;
					transferCountAllTime: number;
					/** Total payments submitted to Akahu (processing/clearing/settled/failed, not deleted). Audit counter for Akahu per-payment charging. */
					paymentCountAllTime: number;
					/** Sum of roundup amounts not yet moved by the transfer job */
					pendingRoundupTotal: number;
					pendingRoundupTxCount: number;
					/** Failed generic + roundup Akahu payments */
					failedTransferCount: number;
				};
				/** Top active savings goals for home (max 2) */
				goals: ApiGoal[];
				/** Pending payment-rule transfers for NextTransferCard (same shape as GET /transfers subset) */
				pendingGenericTransfers: GenericTransfer[];
				recentTransactions: Transaction[];
			}>("/dashboard"),

		transfers: () => request<{ transfers: Transfer[] }>("/dashboard/transfers"),

		/** Spending transactions waiting for the next batched Akahu transfer */
		queue: () => request<{ items: QueuedRoundupTransaction[] }>("/dashboard/queue"),

		removeFromQueue: (transactionId: string) =>
			request<{ message: string }>(`/dashboard/queue/${transactionId}`, { method: "DELETE" }),

		removeManyFromQueue: (ids: string[]) =>
			request<{ message: string; removed: number }>("/dashboard/queue/batch", {
				method: "POST",
				body: JSON.stringify({ ids }),
			}),

		/** Clear Activity rows for a failed Akahu roundup batch (has `roundup_transferred_at` set). Prefer `batchId`. */
		dismissFailedRoundupBatch: (payload: { batchId: string } | { ids: string[] }) =>
			request<{ message: string; dismissed: number }>("/dashboard/queue/dismiss-failed-batch", {
				method: "POST",
				body: JSON.stringify(payload),
			}),

		/** Reset a failed round-up transfer batch to pending and submit payment again. */
		retryFailedRoundupBatch: (batchId: string) =>
			request<{ ok: boolean; retried: number; skipped: number; message: string }>("/dashboard/queue/retry-failed-batch", {
				method: "POST",
				body: JSON.stringify({ batchId }),
			}),

		toggleExcludeQueue: (ids: string[], excluded: boolean) =>
			request<{ updated: number; excluded: boolean }>("/dashboard/queue/exclude", {
				method: "POST",
				body: JSON.stringify({ ids, excluded }),
			}),

		deleteQueueItems: (ids: string[]) =>
			request<{ deleted: number }>("/dashboard/queue/delete", {
				method: "POST",
				body: JSON.stringify({ ids, confirmed: true }),
			}),

		clearQueue: () =>
			request<{ message: string; removed: number }>("/dashboard/queue", { method: "DELETE" }),

		/** TEMPORARY: runs global pending-roundup job (all users). */
		processPendingRoundups: () =>
			request<{ ok: boolean; message: string }>("/dashboard/process-pending-roundups", {
				method: "POST",
			}),

		spendableBalances: () =>
			request<{ accounts: SpendableBalance[] }>("/dashboard/spendable-balances"),
	},

	// ── Accounts ───────────────────────────────────────────────────────────────
	accounts: {
		list: () => request<{ accounts: Account[] }>("/accounts"),

		patch: (id: string, body: { displayName: string | null }) =>
			request<{ account: Account }>(`/accounts/${id}`, {
				method: "PATCH",
				body: JSON.stringify(body),
			}),
	},

	// ── Rules (roundup config) ──────────────────────────────────────────────────
	rules: {
		get: () => request<{ config: RoundupConfig | null }>("/rules"),

		save: (body: RoundupConfigInput) =>
			request<{ message: string; configId: string }>("/rules", {
				method: "PUT",
				body: JSON.stringify(body),
			}),

		toggle: (active: boolean) =>
			request<{ message: string }>("/rules", { method: "PATCH", body: JSON.stringify({ active }) }),

		delete: () => request<{ message: string }>("/rules", { method: "DELETE" }),
	},

	// ── Payment Rules (generic rules engine) ──────────────────────────────────
	paymentRules: {
		list: () => request<{ rules: PaymentRule[] }>("/payment-rules"),

		create: (body: PaymentRuleInput) =>
			request<{ message: string; ruleId: string }>("/payment-rules", {
				method: "POST",
				body: JSON.stringify(body),
			}),

		update: (id: string, body: PaymentRuleInput) =>
			request<{ message: string }>(`/payment-rules/${id}`, {
				method: "PUT",
				body: JSON.stringify(body),
			}),

		toggle: (id: string, active: boolean) =>
			request<{ message: string }>(`/payment-rules/${id}`, {
				method: "PATCH",
				body: JSON.stringify({ active }),
			}),

		delete: (id: string) =>
			request<{ message: string }>(`/payment-rules/${id}`, { method: "DELETE" }),

		processRule: (id: string) =>
			request<{
				ok?: boolean;
				message?: string;
				processed: number;
				failed: number;
				insufficientBalance?: boolean;
				error?: string;
				code?: string;
			}>(`/payment-rules/${id}/process`, { method: "POST" }),

		// ── Drafts (unpublished, non-executing) ──────────────────────────────────
		drafts: {
			list: () => request<{ drafts: RuleDraft[] }>("/payment-rules/drafts"),

			create: (body: { name?: string; payload: Record<string, unknown> }) =>
				request<{ message: string; draftId: string }>("/payment-rules/drafts", {
					method: "POST",
					body: JSON.stringify(body),
				}),

			update: (id: string, body: { name?: string; payload: Record<string, unknown> }) =>
				request<{ message: string; draftId: string }>(`/payment-rules/drafts/${id}`, {
					method: "PUT",
					body: JSON.stringify(body),
				}),

			delete: (id: string) =>
				request<{ message: string }>(`/payment-rules/drafts/${id}`, { method: "DELETE" }),
		},
	},

	// ── Payees ────────────────────────────────────────────────────────────────
	payees: {
		list: () => request<{ payees: Payee[] }>("/payees"),

		create: (body: PayeeInput) =>
			request<{ message: string; payeeId: string }>("/payees", {
				method: "POST",
				body: JSON.stringify(body),
			}),

		update: (id: string, body: PayeeInput) =>
			request<{ message: string }>(`/payees/${id}`, {
				method: "PUT",
				body: JSON.stringify(body),
			}),

		delete: (id: string) => request<{ message: string }>(`/payees/${id}`, { method: "DELETE" }),
	},

	// ── Payee providers (built-in bank bill-payment targets) ─────────────────
	payeeProviders: {
		list: () => request<{ providers: PayeeProvider[] }>("/payee-providers"),
		get: (id: string) => request<{ provider: PayeeProvider & { active: boolean } }>(`/payee-providers/${id}`),
	},

	// ── Payee payment consents ────────────────────────────────────────────────
	payeeConsents: {
		list: () => request<{ consents: PayeeConsent[] }>("/payee-consents"),
		pairs: () => request<{ pairs: { fromAccountId: string; toAccountId: string }[] }>("/payee-consents/pairs"),
		checkEligibility: (accountIds: string[]) =>
			request<{ eligible: string[] }>("/payee-consents/check-eligibility", {
				method: "POST",
				body: JSON.stringify({ accountIds }),
			}),
		initiate: (body: {
			fromAccountId: string;
			payeeId?: string;
			accountId?: string;
			providerId?: string;
			singleLimit?: number;
			periodicLimit?: number;
			periodicFrequency?: "DAILY" | "WEEKLY" | "FORTNIGHTLY" | "MONTHLY" | "ANNUALLY";
			returnTo?: string;
		}) =>
			request<{ consentUrl: string; state: string }>("/payee-consents/initiate", {
				method: "POST",
				body: JSON.stringify(body),
			}),
		revoke: (id: string) =>
			request<{ revoked: true }>(`/payee-consents/${id}`, {
				method: "DELETE",
			}),
		revokeAll: () =>
			request<{ revokedCount: number; failedCount: number }>("/payee-consents/all", {
				method: "DELETE",
			}),
	},

	// ── KiwiSaver ─────────────────────────────────────────────────────────────
	kiwisaver: {
		list: () => request<{ kiwisaverAccounts: KiwisaverAccount[] }>("/kiwisaver"),

		create: (body: KiwisaverAccountInput) =>
			request<{ message: string; kiwisaverId: string }>("/kiwisaver", {
				method: "POST",
				body: JSON.stringify(body),
			}),

		update: (id: string, body: KiwisaverAccountInput) =>
			request<{ message: string }>(`/kiwisaver/${id}`, {
				method: "PUT",
				body: JSON.stringify(body),
			}),

		delete: (id: string) => request<{ message: string }>(`/kiwisaver/${id}`, { method: "DELETE" }),
	},

	// ── Transactions (bank) ──────────────────────────────────────────────────
	transactions: {
		list: (opts?: { days?: number; from?: string; to?: string }) => {
			const qs = new URLSearchParams();
			if (opts?.days != null) qs.set("days", String(opts.days));
			if (opts?.from) qs.set("from", opts.from);
			if (opts?.to) qs.set("to", opts.to);
			const query = qs.toString();
			return request<{ transactions: BankTransaction[] }>(`/transactions${query ? `?${query}` : ""}`);
		},
		categories: () => request<{ categories: CategoryOption[] }>("/transactions/categories"),
	},

	// ── Automation Suggestions ───────────────────────────────────────────────
	suggestions: {
		list: () => request<{ suggestions: Suggestion[] }>("/suggestions"),
		roundupOptimisation: () =>
			request<{
				simulations: IntervalSimulation[];
				currentInterval: number | null;
				hasConfig: boolean;
				insufficientData?: boolean;
				roundupRuleId?: string | null;
			}>("/suggestions/roundup-optimisation"),
	},

	// ── Savings goals ─────────────────────────────────────────────────────────
	goals: {
		list: () => request<{ goals: ApiGoal[] }>("/goals"),
		create: (body: GoalInput) =>
			request<{ goal: ApiGoal }>("/goals", {
				method: "POST",
				body: JSON.stringify(body),
			}),
		update: (id: string, body: Partial<GoalInput> & { archived?: boolean; useAccountBalance?: boolean }) =>
			request<{ goal: ApiGoal | null }>(`/goals/${id}`, {
				method: "PATCH",
				body: JSON.stringify(body),
			}),
		archive: (id: string) =>
			request<{ message: string }>(`/goals/${id}`, { method: "DELETE" }),
	},

	// ── Subscriptions (recurring payments) ───────────────────────────────────
	subscriptions: {
		list: () => request<SubscriptionListResponse>("/subscriptions"),

		detect: () =>
			request<{ detected: number; new: number; verified: number }>("/subscriptions/detect", {
				method: "POST",
				body: JSON.stringify({}),
			}),

		create: (body: SubscriptionCreateInput) =>
			request<{ subscription: ApiSubscription }>("/subscriptions", {
				method: "POST",
				body: JSON.stringify(body),
			}),

		update: (id: string, body: SubscriptionUpdateInput) =>
			request<{ success: boolean }>(`/subscriptions/${id}`, {
				method: "PATCH",
				body: JSON.stringify(body),
			}),

		delete: (id: string, opts?: { excludeFutureDetection?: boolean }) =>
			request<{ success: boolean }>(`/subscriptions/${id}`, {
				method: "DELETE",
				body: JSON.stringify({ excludeFutureDetection: opts?.excludeFutureDetection === true }),
			}),

		transactions: (id: string) =>
			request<SubscriptionDetailResponse>(`/subscriptions/${id}/transactions`),

		insufficientFundsCheck: () =>
			request<{ warnings: SubscriptionFundsWarning[] }>("/subscriptions/insufficient-funds-check"),

		insufficientFundsWarnings: () =>
			request<{ warnings: SubscriptionFundsWarning[] }>("/subscriptions/insufficient-funds-warnings"),

		dismissInsufficientFundsWarning: (id: string) =>
			request<{ success: boolean }>(`/subscriptions/insufficient-funds-warnings/${id}/dismiss`, {
				method: "POST",
			}),
	},

	// ── AI ───────────────────────────────────────────────────────────────────
	ai: {
		parseRule: (prompt: string) =>
			request<{ rule: ParsedRuleDraft }>("/ai/parse-rule", {
				method: "POST",
				body: JSON.stringify({ prompt }),
			}),
	},

	// ── Transfers ─────────────────────────────────────────────────────────────
	transfers: {
		list: (opts?: { from?: string; to?: string }) => {
			const qs = new URLSearchParams();
			if (opts?.from) qs.set("from", opts.from);
			if (opts?.to) qs.set("to", opts.to);
			const query = qs.toString();
			return request<{ transfers: GenericTransfer[] }>(`/transfers${query ? `?${query}` : ""}`);
		},
		process: () =>
			request<{ ok: boolean; message: string }>("/transfers/process", { method: "POST" }),
		/** Reset failed rule transfers to pending and submit payment again (Review tab). */
		retryFailed: (ids: string[]) =>
			request<{ ok: boolean; retried: number; skipped: number; message: string }>("/transfers/retry", {
				method: "POST",
				body: JSON.stringify({ ids }),
			}),
		batches: (opts?: { from?: string; to?: string }) => {
			const qs = new URLSearchParams();
			if (opts?.from) qs.set("from", opts.from);
			if (opts?.to) qs.set("to", opts.to);
			const query = qs.toString();
			return request<{ batches: TransferBatch[] }>(`/transfers/batches${query ? `?${query}` : ""}`);
		},
		toggleExcludePayment: (ids: string[], excluded: boolean) =>
			request<{ updated: number; excluded: boolean }>("/transfers/exclude", {
				method: "POST",
				body: JSON.stringify({ ids, excluded }),
			}),
		deletePending: (ids: string[]) =>
			request<{ deleted: number }>("/transfers/delete", {
				method: "POST",
				body: JSON.stringify({ ids, confirmed: true }),
			}),
		/** Remove failed generic-rule transfer rows (Review tab). */
		deleteFailed: (ids: string[]) =>
			request<{ deleted: number }>("/transfers/delete-failed", {
				method: "POST",
				body: JSON.stringify({ ids }),
			}),
	},

	// ── Admin ──────────────────────────────────────────────────────────────────
	admin: {
		check: () => request<{ isAdmin: boolean }>("/admin/check"),

		whitelist: {
			list: () => request<{ whitelist: WhitelistEntry[] }>("/admin/whitelist"),
			add: (email: string) =>
				request<{ entry: WhitelistEntry }>("/admin/whitelist", {
					method: "POST",
					body: JSON.stringify({ email }),
				}),
			remove: (id: string) =>
				request<{ message: string }>(`/admin/whitelist/${id}`, { method: "DELETE" }),
		},

		waitlist: {
			list: () => request<{ waitlist: WaitlistEntry[] }>("/admin/waitlist"),
		},

		config: {
			get: () => request<{ config: AppConfigMap }>("/admin/config"),
			update: (body: Partial<Record<"akahu_app_secret", string>>) =>
				request<{ message: string }>("/admin/config", {
					method: "PUT",
					body: JSON.stringify(body),
				}),
		},

		auditLogs: (opts?: { limit?: number; action?: string }) => {
			const params = new URLSearchParams();
			if (opts?.limit) params.set("limit", String(opts.limit));
			if (opts?.action) params.set("action", opts.action);
			const qs = params.size > 0 ? `?${params}` : "";
			return request<{ logs: AuditLogEntry[] }>(`/admin/audit-logs${qs}`);
		},

		featureFlags: {
			list: () => request<{ flags: AdminFeatureFlag[] }>("/admin/feature-flags"),
			update: (key: string, body: { enabled?: boolean; tierTargeting?: TierTargeting; description?: string }) =>
				request<{ flag: AdminFeatureFlag }>(`/admin/feature-flags/${key}`, {
					method: "PUT",
					body: JSON.stringify(body),
				}),
		},

		testInvoiceEmail: () =>
			request<{ message: string; to: string }>("/admin/test-invoice-email", { method: "POST" }),
	},

	// ── Akahu ──────────────────────────────────────────────────────────────────
	akahu: {
		getConnectUrl: () =>
			request<
				| { mode: "oauth"; url: string }
				| { mode: "full"; url: null }
			>("/akahu/connect"),
		capabilities: () =>
			request<{
				mode: "none" | "oauth";
				hasConnection: boolean;
				canSyncData: boolean;
				canInitiatePayments: boolean;
				hasInactiveAccounts: boolean;
			}>("/akahu/capabilities"),
		sync: () => request<{ message: string }>("/akahu/sync", { method: "POST" }),
		/** Refresh balances, transactions (~364d), and pending snapshot for one linked account */
		syncAccount: (accountId: string) =>
			request<{ message: string }>(`/akahu/sync/account/${accountId}`, { method: "POST" }),
		disconnect: () => request<{ message: string }>("/akahu/disconnect", { method: "DELETE" }),
		disconnectConnection: (connectionId: string) =>
			request<{ message: string; removedAccountCount: number }>(`/akahu/connections/${encodeURIComponent(connectionId)}`, { method: "DELETE" }),
		me: () => request<{ name: string | null }>("/akahu/me"),
	},

	// ── Passkeys (WebAuthn) ────────────────────────────────────────────────────
	passkeys: {
		registerOptions: (name?: string) =>
			request<Record<string, unknown>>("/auth/passkey/register/options", {
				method: "POST",
				body: JSON.stringify({ name }),
			}),

		registerVerify: (response: Record<string, unknown>, name?: string) =>
			request<{ ok: boolean; passkeyId: string }>("/auth/passkey/register/verify", {
				method: "POST",
				body: JSON.stringify({ response, name }),
			}),

		hasCredentials: (email: string) =>
			request<{ hasPasskeys: boolean }>(`/auth/passkey/has-credentials?email=${encodeURIComponent(email)}`, {
				method: "GET",
				skipAuth: true,
			}),

		loginOptions: (email: string) =>
			request<Record<string, unknown>>("/auth/passkey/login/options", {
				method: "POST",
				body: JSON.stringify({ email }),
				skipAuth: true,
			}),

		loginVerify: (email: string, response: Record<string, unknown>) =>
			request<{ accessToken: string }>("/auth/passkey/login/verify", {
				method: "POST",
				body: JSON.stringify({ email, response }),
				skipAuth: true,
			}),

		hasAny: () => request<{ hasPasskeys: boolean }>("/auth/passkeys/any"),

		list: () => request<{ passkeys: PasskeyInfo[] }>("/auth/passkeys"),

		delete: (id: string) => request<{ ok: boolean }>(`/auth/passkeys/${id}`, { method: "DELETE" }),

		rename: (id: string, name: string) =>
			request<{ ok: boolean }>(`/auth/passkeys/${id}`, {
				method: "PATCH",
				body: JSON.stringify({ name }),
			}),
	},

	// ── Notifications ──────────────────────────────────────────────────────────
	notifications: {
		list: () =>
			request<{ notifications: AppNotification[]; unreadCount: number }>("/notifications"),

		unreadCount: () => request<{ unreadCount: number }>("/notifications/unread-count"),

		markRead: (id: string) =>
			request<{ ok: boolean }>(`/notifications/${id}/read`, { method: "PATCH" }),

		markAllRead: () => request<{ ok: boolean }>("/notifications/read-all", { method: "POST" }),

		test: () => request<{ ok: boolean }>("/notifications/test", { method: "POST" }),
	},

	notificationPreferences: {
		get: () => request<{ preferences: NotificationPreferences }>("/notification-preferences"),
		update: (preferences: Partial<NotificationPreferences>) =>
			request<{ preferences: NotificationPreferences }>("/notification-preferences", {
				method: "PUT",
				body: JSON.stringify({ preferences }),
			}),
	},

	activityLog: {
		list: (limit = 50) =>
			request<{ events: UserActivityEvent[] }>(`/activity-log?limit=${encodeURIComponent(limit)}`),
	},

	exports: {
		downloadTransfersCsv: (from: string, to: string) =>
			downloadFile(`/exports/transfers?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
		downloadActivityCsv: (from: string, to: string) =>
			downloadFile(`/exports/activity?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
	},

	// ── Billing / tier ───────────────────────────────────────────────────────
	// Scaffolding endpoints — no Stripe wiring yet. See docs/pricing.md §11.
	billing: {
		me: () => request<BillingMeResponse>("/billing/me"),
		startTrial: () =>
			request<{ ok: boolean; startedAt?: string; lengthDays?: number; skipped?: string }>(
				"/billing/trial/start",
				{ method: "POST" }
			),
		currentPeriod: () => request<BillingCurrentPeriodResponse>("/billing/current-period"),
		projected: () => request<BillingProjectedResponse>("/billing/projected"),
	},

	featureFlags: {
		list: () => request<Record<string, boolean>>("/feature-flags"),
	},
	demo: {
		/** Start a disposable, fully provisioned live-demo session. */
		start: () =>
			request<{ accessToken: string; user: DemoSessionUser }>("/demo/session", {
				method: "POST",
				skipAuth: true,
			}),
		/** Replace the current fictional workspace with a fresh seeded one. */
		reset: () =>
			request<{ accessToken: string; user: DemoSessionUser }>("/demo/reset", {
				method: "POST",
			}),
		/** Delete the disposable workspace and clear its refresh cookie. */
		end: () => request<{ message: string }>("/demo/session", { method: "DELETE" }),
		/** Demo Akahu "connections hub": list connected banks (grouped). */
		connections: () =>
			request<{ connections: DemoConnection[] }>("/demo/connections"),
		/** Accounts for one demo connection (connection-detail screen). */
		connectionDetail: (connectionId: string) =>
			request<{ connectionName: string; accounts: DemoConnectionAccount[] }>(
				`/demo/connections/${encodeURIComponent(connectionId)}`,
			),
		/** Add the next connection (bank the user picked labels it). Returns the refreshed list. */
		addConnection: (bankId: string) =>
			request<{ connections: DemoConnection[] }>("/demo/connections", {
				method: "POST",
				body: JSON.stringify({ bankId }),
			}),
		/** Remove a connected bank and its demo data. Returns the refreshed list. */
		removeConnection: (connectionId: string) =>
			request<{ connections: DemoConnection[] }>(
				`/demo/connections/${encodeURIComponent(connectionId)}`,
				{ method: "DELETE" },
			),
		/** Enable/disable payments (PAYMENT_FROM) on a demo account. */
		setAccountPayments: (akahuId: string, enabled: boolean) =>
			request<{ akahuId: string; paymentEnabled: boolean }>(
				`/demo/accounts/${encodeURIComponent(akahuId)}/payments`,
				{ method: "POST", body: JSON.stringify({ enabled }) },
			),
		/** Funding account + payees for a pending demo payment consent (authorize screen). */
		consentPreview: (state: string) =>
			request<{
				fundingAccountName: string;
				fundingAccountNumber: string | null;
				payees: { name: string }[];
			}>(`/demo/consent-preview?state=${encodeURIComponent(state)}`),
	},
};

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DemoConnection {
	connectionId: string;
	connectionName: string;
	accountCount: number;
	paymentEnabledCount: number;
}

export interface DemoConnectionAccount {
	akahuId: string;
	name: string;
	formattedAccount: string | null;
	paymentEnabled: boolean;
	paymentSupported: boolean;
}

export interface DemoSessionUser {
	id: string;
	email: string;
	displayName: string | null;
	isDemo: boolean;
	tier: string;
}

export interface BillingLineItem {
	rule_id: string;
	rule_name: string;
	active_days: number;
	days_in_period: number;
	amount_cents: number;
	type?: "rule" | "allowance" | "cap";
	free_rule_days?: number;
}

export interface BillingCurrentPeriodResponse {
	periodStart: string;
	periodEnd: string;
	lineItems: BillingLineItem[];
	subtotalCents: number;
	gstCents: number;
	totalCents: number;
	totalNzd: string;
	freeRuleDays: number;
	allowanceApplied: boolean;
	capApplied: boolean;
	firstRuleFree: boolean;
}

export interface BillingProjectedResponse {
	ruleCount: number;
	projectedCents: number;
	projectedNzd: string;
	firstRuleFree: boolean;
	capApplied: boolean;
	freeRuleAllowance: number;
	monthlyCapNzd: number;
}

export interface GoalDestinationRow {
	goalId: string;
	toAccountId: string | null;
	toPayeeId: string | null;
	toKiwisaverId: string | null;
	percentage: number;
	createdAt: string;
	name: string | null;
	bankName: string | null;
	bankLogoUrl: string | null;
	balance: number | null;
}

export interface UserActivityEvent {
	id: string;
	kind:
		| "rules"
		| "payees"
		| "subscriptions"
		| "settings"
		| "accounts"
		| "goals"
		| "kiwisaver"
		| "banking"
		| "billing"
		| "transfers";
	title: string;
	description: string;
	details: { label: string; value: string; section?: string; previousValue?: string }[];
	actor?: { userId: string; displayName: string };
	resource?: { type: string; id?: string; displayName: string };
	table?: {
		sections: Array<{
			id: string;
			label: string;
			rows: Array<{
				id: string;
				label: string;
				previous?: string;
				next: string;
				valueKind?: "text" | "money" | "percent" | "date" | "status" | "masked";
				importance?: "normal" | "high";
			}>;
		}>;
	};
	changeSet?:
		| {
				domain: "rule";
				sections: Array<{
					id: string;
					label: string;
					items: Array<{
						type: "fieldChanged";
						label: string;
						field: string;
						previous?: string;
						next: string;
					}>;
				}>;
		  }
		| {
				domain: "payee" | "subscription" | "goal" | "kiwisaver" | "settings" | "account" | "billing" | "banking" | "generic";
				sections: Array<{
					id: string;
					label: string;
					rows: Array<{
						id: string;
						label: string;
						previous?: string;
						next: string;
						valueKind?: "text" | "money" | "percent" | "date" | "status" | "masked";
						importance?: "normal" | "high";
					}>;
				}>;
		  };
	createdAt: string;
	href?: string;
}

/** API shape for savings goals (GET /goals, dashboard.goals) */
export interface ApiGoal {
	id: string;
	userId: string;
	name: string;
	icon: string;
	targetAmount: number;
	deadline: string | null;
	completedAt: string | null;
	archived: boolean;
	useAccountBalance: boolean;
	createdAt: string;
	updatedAt: string;
	destinations: GoalDestinationRow[];
	currentAmount: number;
	percentageTowardTarget: number;
	remaining: number;
	weeklyRate: number | null;
	weeksToGo: number | null;
}

export interface GoalInput {
	name: string;
	icon?: string;
	targetAmount: number;
	deadline?: string;
	destinations: {
		accountId?: string;
		payeeId?: string;
		kiwisaverId?: string;
		percentage?: number;
	}[];
}

export type SubscriptionFrequency = "weekly" | "fortnightly" | "monthly" | "quarterly" | "yearly";
export type SubscriptionStatus = "detected" | "confirmed" | "paused" | "cancelled" | "dismissed";

export interface ApiSubscription {
	id: string;
	userId: string;
	merchantName: string;
	akahuMerchantId: string | null;
	categoryGroup: string | null;
	amount: string;
	currency: string;
	frequency: SubscriptionFrequency;
	confidence: string;
	firstDetectedAt: string;
	lastTransactionId: string | null;
	lastTransactionDate: string | null;
	nextExpectedDate: string | null;
	status: SubscriptionStatus;
	ruleCreated: boolean;
	ruleId: string | null;
	accountId: string | null;
	accountName?: string | null;
	userConfirmedAt: string | null;
	userLabel: string | null;
	notes: string | null;
	/** Akahu enrichment merchant logo (same as transaction `logoUrl` on Activity). */
	merchantLogoUrl?: string | null;
	createdAt: string;
	updatedAt: string;
}

export interface SubscriptionListResponse {
	subscriptions: ApiSubscription[];
	totals: {
		weekly: string;
		monthly: string;
		yearly: string;
		monthlyEquivalent: string;
	};
}

export interface SubscriptionCreateInput {
	merchantName: string;
	amount: string;
	frequency: SubscriptionFrequency;
	accountId?: string | null;
	userLabel?: string;
	notes?: string;
	nextExpectedDate?: string;
}

export interface SubscriptionUpdateInput {
	merchantName?: string;
	amount?: string;
	frequency?: SubscriptionFrequency;
	status?: SubscriptionStatus;
	accountId?: string | null;
	userLabel?: string | null;
	notes?: string | null;
	nextExpectedDate?: string | null;
}

export interface SubscriptionTxRow {
	id: string;
	date: string;
	amount: string;
	description: string | null;
	merchantName: string | null;
	categoryGroup: string | null;
	accountName: string;
}

export interface SubscriptionDetailResponse {
	subscription: ApiSubscription;
	transactions: SubscriptionTxRow[];
	stats: {
		count: number;
		avgAmount: string;
		minAmount: string;
		maxAmount: string;
		totalSpent: string;
	};
}

export interface AppNotification {
	id: string;
	type: string;
	title: string;
	message: string;
	data: Record<string, unknown> | null;
	read: boolean;
	createdAt: string;
}

export interface SubscriptionFundsWarningItem {
	id: string;
	merchantName: string;
	displayName: string;
	amount: string;
}

export interface SubscriptionFundsWarning {
	id: string;
	warningDate: string;
	accountId: string;
	accountName: string;
	totalAmount: string;
	availableBalance: string;
	subscriptions: SubscriptionFundsWarningItem[];
}

export type NotificationPreferenceType =
	| "payment_pending_approval"
	| "transaction_captured"
	| "batch_processed"
	| "goal_completed"
	| "insufficient_funds";

export type NotificationPreferences = Record<NotificationPreferenceType, boolean>;

export interface Account {
	id: string;
	name: string;
	/** Optional in-app label; falls back to `name` (Akahu) everywhere in the UI. */
	displayName: string | null;
	type: string;
	/** Akahu account id (e.g. acc_...) */
	akahuId?: string;
	/** Provider/connection id from Akahu (e.g. conn_...) */
	akahuConnectionId?: string | null;
	bankName: string | null;
	/** Provider logo URL from Akahu CDN — display next to the user's own account in-app. */
	bankLogoUrl: string | null;
	/** NZ bank account number e.g. "01-2345-6789012-34" */
	formattedAccount: string | null;
	balance: string | null;
	balanceAvailable?: string | null;
	currency: string;
	/** When balances were last updated from the provider */
	lastSyncedAt: string | null;
	/** Set when Akahu no longer returns this account for the user (revoked/unlinked). */
	akahuDisconnectedAt?: string | null;
	/** From Akahu GET /accounts `attributes` (updated on sync). */
	akahuPaymentFrom?: boolean;
	akahuPaymentTo?: boolean;
	akahuTransferFrom?: boolean;
	akahuTransferTo?: boolean;
	/** Present when Akahu has synced official per-account payment-consent data. */
	akahuPaymentConsentSyncedAt?: string | null;
	/** True when a payment consent is currently granted for this account. */
	akahuPaymentConsentEnabled?: boolean;
	akahuPaymentSingleLimit?: string | null;
	akahuPaymentPeriodicLimit?: string | null;
	akahuPaymentPeriodicFrequency?: string | null;
}

export interface SpendableBalance {
	id: string;
	name: string;
	bankName: string | null;
	bankLogoUrl: string | null;
	akahuConnectionId: string | null;
	balance: string;
	pendingTransfers: string;
	availableToSpend: string;
	lastSyncedAt: string | null;
}

export interface AccountInfo {
	id: string;
	name: string;
	bankName: string | null;
}

export interface PayeeInfo {
	id: string;
	accountNumber: string;
	name: string;
	nickname: string | null;
}

export interface RoundupConfigTarget {
	account?: AccountInfo;
	payee?: PayeeInfo;
	percentage: number;
}

export interface RoundupConfig {
	id: string;
	fromAccount: AccountInfo;
	interval: "1" | "2" | "5" | "10";
	active: boolean;
	trackingAccounts: AccountInfo[];
	targets: RoundupConfigTarget[];
	createdAt: string;
	updatedAt: string;
}

export type RoundupConfigInputTarget =
	| { accountId: string; percentage: number }
	| { payeeId: string; percentage: number }
	| { payee: { accountNumber: string; name: string }; percentage: number };

export interface RoundupConfigInput {
	fromAccountId: string;
	trackingAccountIds: string[];
	targets: RoundupConfigInputTarget[];
	interval: "1" | "2" | "5" | "10";
	active?: boolean;
}

export interface Transaction {
	id: string;
	amount: string;
	description: string | null;
	merchantName: string | null;
	category: string | null;
	/** Merchant logo URL from Akahu CDN — display in-app alongside transaction. */
	logoUrl: string | null;
	/** High-level spending category (e.g. "Food", "Transport", "Lifestyle"). */
	categoryGroup: string | null;
	date: string;
	roundupAmount: string | null;
	roundupTransferredAt: string | null;
	/** `transferred` / `sent` / `failed` / `queued` (legacy responses may still send `initiated`) */
	roundupStatus?: "transferred" | "sent" | "queued" | "initiated" | "failed" | null;
	accountName: string;
	bankName: string | null;
	/** True when row comes from Akahu pending (not yet posted to settled feed) */
	settlementPending?: boolean;
}

/** Funding → destination as executed for this batch (`transfers` under `transfer_batches`), for UI labels */
/** One destination within a batch snapshot — aggregated from the actual transfer rows (historical truth, not live rule config). */
export interface RoundupBatchDestination {
	kind: "account" | "payee" | "kiwisaver";
	targetAccountId: string | null;
	targetPayeeId: string | null;
	targetKiwisaverId: string | null;
	name: string;
	bankName: string | null;
	/** Sum of `transfers.amount` for this destination in the batch (decimal string). */
	amount: string;
	/** Share of the batch total as a percentage (0-100, rounded to 2dp). */
	pct: number;
}

export interface RoundupBatchRoute {
	fromAccountId: string;
	fromAccountName: string;
	fromBankName: string | null;
	/** @deprecated Prefer `destinations`; retained for legacy single-destination code paths. */
	toAccountId: string | null;
	/** @deprecated Prefer `destinations`. */
	toPayeeId: string | null;
	toDestinationLabel: string;
	/** @deprecated Prefer `destinations[0].bankName`. */
	toBankName: string | null;
	/**
	 * Historical split for this batch, reconstructed from the actual
	 * transfer rows at batch time (one entry per distinct destination).
	 * Backends older than this change may omit the field — treat `undefined`
	 * as "snapshot not available, fall back to rule actions".
	 */
	destinations?: RoundupBatchDestination[];
}

/** Row in GET /dashboard/queue — roundup in flight until Akahu batch is fully settled */
export interface QueuedRoundupTransaction {
	id: string;
	amount: string;
	description: string | null;
	merchantName: string | null;
	category: string | null;
	logoUrl: string | null;
	categoryGroup: string | null;
	date: string;
	/** Null for bank-pending rows (unsettled auth); UI derives potential from rules interval */
	roundupAmount: string | null;
	roundupExcluded: boolean;
	/** 'internal_transfer' | 'roundup_payment' | null — why this was auto-excluded */
	exclusionReason: string | null;
	/** Payment submitted; row stays until payment poll marks all batch transfers settled/failed */
	pendingAkahuTransfer?: boolean;
	/**
	 * `queued` = not yet sent to Akahu; `null` = bank-pending snapshot. `initiated` is accepted from
	 * older servers and coerced to `queued` in `normalizeQueuedRoundupTransaction`.
	 */
	roundupStatus: "queued" | "transferred" | "sent" | "initiated" | "failed" | null;
	accountName: string;
	bankName: string | null;
	/** Akahu pending snapshot — not in DB queue until the bank posts the transaction */
	settlementPending?: boolean;
	/** Akahu status text or error message when roundupStatus = 'failed' (from audit_logs) */
	failureReason?: string | null;
	/** Set when this tx was included in a roundup batch */
	roundupBatchId?: string | null;
	/** When we created the `transfer_batches` row for this tx's batch (`transfer_batches.created_at`) — Activity uses it with spend dates as fallback. */
	roundupTransferredAt?: string | null;
	/** Present when `roundupBatchId` is a `transfer_batches` id — use for settled/failed batch labels */
	roundupBatchRoute?: RoundupBatchRoute | null;
	/** Max retry count across transfer rows linked to this source transaction. */
	retryCount?: number;
}

export interface Transfer {
	id: string;
	amount: string;
	status: string;
	processedAt: string | null;
	createdAt: string;
	fromAccount: string;
}

// ─── Payment Rules Types ──────────────────────────────────────────────────────

export interface RuleCondition {
	id?: string;
	field:
		| "account"
		| "direction"
		| "amount"
		| "description"
		| "merchant"
		| "category"
		| "category_group"
		| "particulars"
		| "code"
		| "reference"
		| "balance";
	operator:
		| "equals"
		| "not_equals"
		| "contains"
		| "not_contains"
		| "greater_than"
		| "less_than"
		| "between"
		| "regex";
	value: string;
	valueSecondary?: string;
	/**
	 * Conditions inside the same group index are ANDed; different group
	 * indices OR across (OR-of-ANDs). `0` for single-group rules, which is
	 * the majority of rules created before the groups feature landed.
	 */
	groupIndex?: number;
	/**
	 * Only populated for balance-mode conditions (`field === 'balance'`):
	 * the watched account whose effective balance is compared against
	 * `value` / `valueSecondary`. Null/undefined for every other field.
	 */
	subjectAccountId?: string | null;
}

/** Canonical grouped shape: rows inside `conditions` are ANDed; groups OR across. */
export interface RuleConditionGroup {
	conditions: RuleCondition[];
}

export interface RuleAction {
	id?: string;
	operation:
		| "deduct_percentage"
		| "deduct_fixed"
		| "add_percentage"
		| "add_fixed"
		| "remainder"
		| "roundup"
		| "full_amount"
		| "fixed_amount"
		| "top_up_to_target"
		| "sweep_above";
	/** Percentage or dollar amount; may be string from Postgres NUMERIC */
	value?: number | string | null;
	/** '%' = `value` is a % of the distribution pool; '$' = `value` is a fixed NZD amount. */
	valueType?: "%" | "$";
	targetType: "account" | "payee" | "kiwisaver";
	targetAccountId?: string;
	targetPayeeId?: string;
	targetKiwisaverId?: string;
	/** Destination PCR fields shown on the payee's bank statement (max 12 chars each, only for targetType === 'payee'). */
	payeeParticulars?: string;
	payeeCode?: string;
	payeeReference?: string;
	/** Set for built-in bank bill-payment providers (XOR with `targetPayeeId` when `targetType === 'payee'`). */
	targetPayeeProviderId?: string;
	/**
	 * Server-computed shoulder-surfing / log-safe view of PCR (may differ from plaintext `payeeParticulars` &amp; al.).
	 * Prefer for read-only UIs; use `payeeParticulars` in the rules editor (decrypted for edit).
	 */
	pcrDisplay?: { payeeParticulars: string | null; payeeCode: string | null; payeeReference: string | null };
	targetPayeeProvider?: {
		id: string;
		slug: string;
		displayName: string;
		bankName: string;
		accountNumber: string;
	} | null;
	label?: string;
	targetAccount?: {
		id: string;
		name: string;
		bankName: string | null;
		akahuId?: string;
		akahuConnectionId?: string | null;
		akahuTransferTo?: boolean;
	} | null;
	targetPayee?: { id: string; name: string; nickname: string | null; accountNumber: string } | null;
	targetKiwisaver?: { id: string; name: string; irdNumber: string } | null;
}

export interface ScheduleConfig {
	type: "nightly" | "weekly" | "fortnightly" | "monthly" | "threshold" | "amount_threshold";
	/** Local wall-clock ISO "YYYY-MM-DDTHH:mm" in `timezone`. Single source of truth for the
	 *  first/next fire instant — weekday, day-of-month, and time-of-day are all derived from it.
	 *  Required for time-based types; unused by accumulator types. */
	firstRunAt?: string;
	timezone?: string;
	threshold?: number;
	amountThreshold?: number;
}

export interface PaymentRule {
	id: string;
	name: string;
	description: string | null;
	active: boolean;
	/** Deprecated — derived from `groups` shape by the server for legacy clients. Prefer `groups`. */
	conditionLogic: "and" | "or";
	triggerMode: "auto" | "manual";
	ruleType: "generic" | "roundup";
	fromAccountId: string | null;
	fromAccount: { id: string; name: string; bankName: string | null } | null;
	ruleConfig: Record<string, unknown> | null;
	/** Canonical grouped conditions. Rows inside a group AND; groups OR across. */
	groups: RuleConditionGroup[];
	/** @deprecated Flat view of every condition row; use `groups` instead. Kept for one release. */
	conditions: RuleCondition[];
	actions: RuleAction[];
	parentRuleId: string | null;
	childRuleIds: string[];
	pendingTransferCount: number;
	pendingTransferTotal: number;
	/** Payments submitted to Akahu for this rule (processing/clearing/settled/failed, not deleted). For audit vs Akahu per-payment charging. */
	submittedTransferCount: number;
	/** Total $ submitted to Akahu for this rule (same scope as submittedTransferCount). */
	submittedTransferTotal: number;
	reminderConfig: {
		type: "specific_date" | "recurring";
		date?: string;
		cadence?: "daily" | "weekly" | "fortnightly" | "monthly" | "yearly";
		timezone?: string;
		lastSentAt?: string;
	} | null;
	createdAt: string;
	updatedAt: string;
}

export interface PaymentRuleInput {
	name: string;
	/** `null` clears the stored description (empty string is treated the same server-side). */
	description?: string | null;
	/** Deprecated — server now derives the legacy flag from `groups`. Safe to omit. */
	conditionLogic?: "and" | "or";
	triggerMode: "auto" | "manual";
	ruleType?: "generic" | "roundup";
	fromAccountId?: string;
	ruleConfig?: Record<string, unknown>;
	schedule?: ScheduleConfig;
	/** Preferred shape. When present, `conditions`/`conditionLogic` are ignored by the server. */
	groups?: Array<{ conditions: Omit<RuleCondition, "id" | "groupIndex">[] }>;
	/** @deprecated legacy flat conditions. Only used when `groups` is absent. */
	conditions: Omit<RuleCondition, "id" | "groupIndex">[];
	actions: Omit<
		RuleAction,
		"id" | "targetAccount" | "targetPayee" | "targetKiwisaver" | "pcrDisplay" | "targetPayeeProvider"
	>[];
	parentRuleId?: string;
	reminderConfig?: {
		type: "specific_date" | "recurring";
		date?: string;
		cadence?: "daily" | "weekly" | "fortnightly" | "monthly" | "yearly";
		timezone?: string;
	} | null;
}

/**
 * An unpublished rule draft. `payload` is an opaque snapshot of the editor form
 * state (produced by the New Rule page's `captureFormState()`), stored verbatim
 * and never validated as a full rule until the user publishes it.
 */
export interface RuleDraft {
	id: string;
	name: string;
	payload: Record<string, unknown>;
	createdAt: string;
	updatedAt: string;
}

// ─── Payees Types ─────────────────────────────────────────────────────────────

export interface Payee {
	id: string;
	userId: string;
	accountNumber: string;
	name: string;
	nickname: string | null;
	verificationAttestedAt: string | null;
	verificationAttestationVersion: string | null;
	createdAt: string;
	updatedAt: string;
}

export interface PayeeInput {
	accountNumber: string;
	name: string;
	nickname?: string;
	verificationAttested: true;
}

export interface PayeeConsent {
	id: string;
	userId: string;
	fromAccountId: string;
	fromAccountName: string | null;
	payeeAccountNumber: string;
	payeeName: string;
	payeeAkahuId: string | null;
	payeeProviderId: string | null;
	consentStatus: "pending_initiation" | "pending_approval" | "active" | "pending_revocation" | "revoked" | "expired";
	akahuConsentId: string | null;
	activatedAt: string | null;
	revokedAt: string | null;
	createdAt: string;
	updatedAt: string;
}

/** `GET /payee-providers` — public bill-payment account info + per-field PCR UI hints. */
export interface PayeeProviderFieldMeta {
	label: string | null;
	placeholder: string | null;
	help: string | null;
	required: boolean;
	/** 'numeric' = digits-only input; 'text' = free text. null = no constraint. */
	inputType: "numeric" | "text" | null;
	/** Regex the value must match (e.g. `^[0-9]{8}$`). null = no pattern constraint. */
	inputPattern: string | null;
	/** Max character count for this field (NZ bank PCR limit is 12). null = 12. */
	inputMaxlength: number | null;
}

export interface PayeeProvider {
	id: string;
	slug: string;
	displayName: string;
	bankName: string;
	accountNumber: string;
	defaultPayeeName: string;
	externalHelpUrl: string | null;
	particulars: PayeeProviderFieldMeta;
	code: PayeeProviderFieldMeta;
	reference: PayeeProviderFieldMeta;
}

// ─── KiwiSaver Types ──────────────────────────────────────────────────────────

export interface KiwisaverAccount {
	id: string;
	userId: string;
	name: string;
	irdNumber: string;
	createdAt: string;
	updatedAt: string;
}

export interface KiwisaverAccountInput {
	name: string;
	irdNumber: string;
}

// ─── AI Types ─────────────────────────────────────────────────────────────────

export interface ParsedRuleDraft {
	name: string;
	description?: string;
	/** Deprecated — derived. The AI adapter also emits `groups`. */
	conditionLogic: "and" | "or";
	triggerMode: "auto" | "manual";
	/** UUID of the account the payment is debited FROM. */
	fromAccountId?: string;
	/** For roundup rules: the rounding interval in NZD (1 | 2 | 5 | 10). */
	roundupInterval?: 1 | 2 | 5 | 10;
	/** Canonical grouped output from the AI adapter. Falls back to `conditions` when missing. */
	groups?: Array<{
		conditions: Array<{
			field: string;
			operator: string;
			value: string;
			valueSecondary?: string;
			/** Required by the API on `field === "balance"` rows; absent on every other field. */
			subjectAccountId?: string;
		}>;
	}>;
	conditions: Array<{
		field: string;
		operator: string;
		value: string;
		valueSecondary?: string;
		subjectAccountId?: string;
	}>;
	/** Free-form ruleConfig hints (esp. `amountOperation` + `amountValue` for fixed_amount / top_up_to_target). */
	ruleConfig?: {
		amountOperation?: string;
		amountValue?: number;
		interval?: string | number;
		executionTiming?: "scheduled" | "eager";
	};
	actions: Array<{
		operation: string;
		value?: number;
		targetType: "account" | "payee" | "kiwisaver";
		targetAccountId?: string;
		targetPayeeId?: string;
		targetPayeeProviderId?: string;
		targetKiwisaverId?: string;
		payeeParticulars?: string;
		payeeCode?: string;
		payeeReference?: string;
		label?: string;
	}>;
	schedule?: {
		type: "nightly" | "weekly" | "fortnightly" | "monthly" | "threshold" | "amount_threshold";
		firstRunAt?: string;
		timezone?: string;
		threshold?: number;
		amountThreshold?: number;
	};
}

// ─── Optimisation Types ───────────────────────────────────────────────────────

export interface IntervalSimulation {
	interval: 1 | 2 | 5 | 10;
	monthlyRoundup: number;
	annualRoundup: number;
	isCurrent: boolean;
}

// ─── Suggestions Types ────────────────────────────────────────────────────────

export interface Suggestion {
	id: string;
	type: "interval_upgrade" | "rule_candidate" | "savings_progress" | "excluded_review" | "untracked_account";
	title: string;
	body: string;
	cta: { label: string; href: string };
	priority: number;
}

// ─── Bank Transactions Types ──────────────────────────────────────────────────

/** A distinct category option returned by GET /transactions/categories. */
export interface CategoryOption {
	/** The stored value — category name for specific entries, group name for group entries. */
	value: string;
	/** Display label (same as value). */
	label: string;
	/** Parent group name for specific categories; null for group-level entries. */
	group: string | null;
	/** 'category' = specific NZFCC name; 'category_group' = broad Akahu group. */
	type: 'category' | 'category_group';
}

export type BankTransactionAccountType =
	| 'checking'
	| 'savings'
	| 'credit'
	| 'loan'
	| 'investment'
	| 'other';

export interface BankTransaction {
	id: string;
	/** Linked source account for this feed row (funding account for debits). */
	accountId: string;
	accountType: BankTransactionAccountType;
	amount: string;
	description: string | null;
	merchantName: string | null;
	category: string | null;
	logoUrl: string | null;
	categoryGroup: string | null;
	/** Akahu `meta.other_account` when the bank feed had it (formatted NZ account). */
	otherAccount: string | null;
	/** NZ bank PCR from feed; null for pending snapshots or when absent. */
	metaParticulars: string | null;
	metaCode: string | null;
	metaReference: string | null;
	date: string;
	accountName: string;
	bankName: string | null;
	bankLogoUrl: string | null;
	settlementPending?: boolean;
}

// ─── Generic Transfers Types ──────────────────────────────────────────────────

export interface GenericTransfer {
	id: string;
	amount: string;
	status: string;
	paymentExcluded?: boolean;
	akahuPaymentId: string | null;
	batchId: string | null;
	/** Source bank transaction this transfer leg was derived from. Null for standing-order synthetic rows. */
	transactionId: string | null;
	processedAt: string | null;
	createdAt: string;
	/** Last row mutation (rule recalc, status change, etc.); matches DB `updated_at`. On insert equals `created_at`. */
	updatedAt?: string;
	/** When the batch was created (i.e. when the payment was sent to Akahu). Null for pending transfers not yet batched. Use this — not createdAt — to determine which day-group the batch belongs to. */
	batchCreatedAt?: string | null;
	ruleName: string | null;
	ruleId: string | null;
	ruleType: string | null;
	actionLabel: string | null;
	actionOperation: string | null;
	fromAccountName: string | null;
	/** Funding account bank (Akahu payment source) */
	fromBankName: string | null;
	/** Destination account for this transfer row (`transfers.to_account_id`) — not live rule */
	toAccountId: string | null;
	toAccountName: string | null;
	toAccountBankName: string | null;
	/** Spend account for the matched transaction (merchant line), when linked */
	sourceTxAccountName: string | null;
	sourceTxBankName: string | null;
	toPayeeName: string | null;
	toPayeeNickname: string | null;
	toPayeeProviderName: string | null;
	toKiwisaverId: string | null;
	toKiwisaverName: string | null;
	sourceTxDescription: string | null;
	sourceTxMerchantName: string | null;
	sourceTxAmount: string | null;
	sourceTxDate: string | null;
	/** 'internal_transfer' | null — from the source transaction's exclusion_reason */
	sourceTxExclusionReason: string | null;
	/** Akahu status text or error message when status = 'failed' (from audit_logs) */
	failureReason: string | null;
	/** User retries from Review; capped server-side (see MAX_MANUAL_TRANSFER_RETRIES in UI). */
	retryCount?: number;
	/**
	 * Balance-watch rules only: the watched account's *effective* balance
	 * (stored balance ± in-flight transfers) at the time the list was fetched.
	 * This is the figure the pre-initiation revalidation checks, so the card can
	 * warn when a manual top-up may no longer be needed. Null for non-balance rules.
	 */
	projectedWatchedBalance?: number | null;
	/** Display name of the watched account (when `projectedWatchedBalance` is set). */
	watchedAccountName?: string | null;
	/** Raw synced balance of the watched account, before in-flight adjustments. */
	watchedAccountStoredBalance?: number | null;
}

export interface TransferBatch {
	id: string;
	status: string;
	totalAmount: string | null;
	createdAt: string;
	ruleName: string | null;
}

// ─── Admin Types ──────────────────────────────────────────────────────────────

export interface WhitelistEntry {
	id: string;
	email: string;
	createdAt: string;
	createdBy: string | null;
	lastLoggedInAt: string | null;
}

export interface WaitlistEntry {
	id: string;
	email: string;
	message: string | null;
	createdAt: string;
}

export type AppConfigMap = Record<"akahu_app_secret", { masked: string; set: boolean }>;

export type TierTargeting = "all" | "standard" | "business";

export interface AdminFeatureFlag {
	key: string;
	enabled: boolean;
	tierTargeting: TierTargeting;
	description: string | null;
	updatedAt: string;
	updatedBy: string | null;
}

export interface PasskeyInfo {
	id: string;
	name: string;
	authenticatorAttachment: string | null;
	backedUp: boolean;
	lastUsedAt: string | null;
	createdAt: string;
}

export interface AuditLogEntry {
	id: string;
	action: string;
	userId: string | null;
	email: string | null;
	ipAddress: string | null;
	metadata: Record<string, unknown> | null;
	createdAt: string;
}

// ─── Billing / tier types ─────────────────────────────────────────────────────

export type BillingTier = "free" | "standard" | "business";

export interface BillingMeResponse {
	tier: BillingTier;
	tierValidUntil: string | null;
	trial: {
		active: boolean;
		startedAt: string | null;
		daysRemaining: number | null;
		consumed: boolean;
		lengthDays: number;
	};
	features: Record<string, boolean>;
	limits: Record<string, number | null>;
	usage: {
		linked_accounts: number;
		active_payment_rules: number;
		savings_goals: number;
		monthly_successful_payments: number;
	};
	catalog: {
		features: Record<BillingTier, Record<string, boolean>>;
		limits: Record<BillingTier, Record<string, number | null>>;
	};
	firstRuleFree: boolean;
}
